
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ref, set } from 'firebase/database';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faUsers,
    faUserCheck,
    faSpinner,
    faReceipt,
    faExclamationCircle,
    faCalendarAlt,
    faEdit,
    faTimes,
    faSave
} from '@fortawesome/free-solid-svg-icons';
import {database} from "../../firebase/firebase.ts";
import {generateId, getLastSplitBetween} from "../../utils/helpers.ts";
import { Expense, ShareRatios, SponsorExpense } from '../../types';
import { useTranslation } from 'react-i18next';
import HelpTip from '../common/HelpTip';
import AmountInput from '../common/AmountInput';
import ParticipantPicker from '../common/ParticipantPicker';
import { useCompactMode } from '../../hooks/useCompactMode';
import { useUserPreferences } from '../../hooks/useUserPreferences';

interface ExpenseFormProps {
    eventId: string;
    participants: { [userId: string]: string };
    /** The event's share ratios, for the picker's per-person preview. */
    shareRatios?: ShareRatios;
    currentUser: string;
    /** Everything already on the bill — only read to reuse this person's last
        split selection. Leave it out and the picker opens on everyone. */
    expenses?: (Expense | SponsorExpense)[];
    onExpenseAdded?: () => void;
    // Edit mode props
    editExpense?: Expense;
    onSave?: (expense: Expense) => Promise<void>;
    onCancel?: () => void;
    loading?: boolean;
    /** Render the fields alone, for a parent that already supplies the title and
        the surface — the mobile add sheet. Ignored in edit mode. */
    bare?: boolean;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
                                                     eventId,
                                                     participants,
                                                     shareRatios,
                                                     currentUser,
                                                     expenses = [],
                                                     onExpenseAdded,
                                                     editExpense,
                                                     onSave,
                                                     onCancel,
                                                     loading = false,
                                                     bare = false
                                                 }) => {
    const { t } = useTranslation();
    const { compactMode } = useCompactMode();
    const { preferences: { splitFirst } } = useUserPreferences();
    const isEditMode = !!editExpense;

    const [amount, setAmount] = useState<string>(() => {
        if (isEditMode) {
            // In edit mode, show amount / 1000 if compact mode is on
            return compactMode ? (editExpense.amount / 1000).toString() : editExpense.amount.toString();
        }
        return '';
    });
    const [description, setDescription] = useState<string>(isEditMode ? editExpense.description : '');
    const [expenseDate, setExpenseDate] = useState<string>(() => {
        if (isEditMode) {
            const date = new Date(editExpense.timestamp);
            return date.toISOString().split('T')[0];
        }
        const now = new Date();
        return now.toISOString().split('T')[0]; // YYYY-MM-DD
    });
    const [payer, setPayer] = useState<string>(isEditMode ? editExpense.payer : (currentUser || ''));
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [formError, setFormError] = useState<string>('');

    // ✅ Sử dụng useMemo để tránh tính toán lại mỗi render
    const participantIds = useMemo(() => Object.keys(participants), [participants]);

    // What a fresh form starts on: whoever this person last split with, or the
    // whole group if this is their first expense here. Read once, when the form
    // opens, so an expense landing in the list never moves the picker under
    // their hands mid-entry.
    const [splitBetween, setSplitBetween] = useState<string[]>(() => {
        if (isEditMode) return editExpense.splitBetween;
        return getLastSplitBetween(expenses, currentUser, participantIds) ?? participantIds;
    });

    // Someone leaving the event must not stay in the split. Anything else the
    // user picked is left alone — this used to reset the whole selection every
    // time the participants object was rebuilt.
    React.useEffect(() => {
        setSplitBetween(prev => {
            const kept = prev.filter(id => participantIds.includes(id));
            if (kept.length === prev.length) return prev;
            return kept.length > 0 ? kept : participantIds;
        });
    }, [participantIds]);

    const isFormDisabled = isSubmitting || loading;

    // Amount typed in K mode means thousands, so the real value is ×1.000
    const enteredAmountVnd = (parseFloat(amount) || 0) * (compactMode ? 1000 : 1);

    // Switching the đ/K unit must not change how much money the typed number means
    const prevCompactMode = React.useRef(compactMode);
    React.useEffect(() => {
        if (prevCompactMode.current === compactMode) return;
        prevCompactMode.current = compactMode;
        setAmount(prev => {
            const parsed = parseFloat(prev);
            if (isNaN(parsed)) return prev;
            return (compactMode ? parsed / 1000 : parsed * 1000).toString();
        });
    }, [compactMode]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        if (!amount || !description || !payer || splitBetween.length === 0) {
            setFormError(t('components.expenseForm.errors.fillAllFields'));
            return;
        }

        const expenseAmount = enteredAmountVnd;
        if (isNaN(expenseAmount) || expenseAmount <= 0) {
            setFormError(t('components.expenseForm.errors.invalidAmount'));
            return;
        }

        setIsSubmitting(true);

        try {
            if (isEditMode && onSave) {
                // Edit mode: update existing expense
                const selectedDate = new Date(expenseDate);
                const originalDate = new Date(editExpense.timestamp);
                selectedDate.setHours(originalDate.getHours(), originalDate.getMinutes(), originalDate.getSeconds(), originalDate.getMilliseconds());
                const timestamp = selectedDate.getTime();

                const updatedExpense: Expense = {
                    ...editExpense,
                    amount: expenseAmount,
                    description,
                    payer,
                    splitBetween,
                    timestamp
                };

                await onSave(updatedExpense);
            } else {
                // Create mode: add new expense
                const expenseId = `ex_${generateId()}`;
                const selectedDate = new Date(expenseDate);
                const now = new Date();
                selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
                const timestamp = selectedDate.getTime();

                const expense = {
                    id: expenseId,
                    payer,
                    amount: expenseAmount,
                    description,
                    splitBetween,
                    timestamp,
                    createdBy: currentUser,
                    editableUntil: Date.now() + (5 * 60 * 1000) // 5 minutes from now
                };

                const expenseRef = ref(database, `eventExpenses/${eventId}/${expenseId}`);
                await set(expenseRef, expense);

                // Reset form
                setAmount('');
                setDescription('');
                // Keep who it was split between: the next expense is usually the
                // same round of people, and that selection is now the history
                // another form would pick up anyway.
                setFormError('');

                onExpenseAdded && onExpenseAdded();
            }
        } catch (error) {
            console.error(isEditMode ? 'Error updating expense:' : 'Error adding expense:', error);
            setFormError(isEditMode
                ? t('event.components.editExpenseForm.errors.updateFailed')
                : t('components.expenseForm.errors.addFailed')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Split between ── placed by the account's "split first" setting
    const splitBetweenField = (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FontAwesomeIcon icon={faUsers} className="mr-2" aria-hidden="true" />
                {t('components.expenseForm.splitBetween')}
                <HelpTip text={t('components.expenseForm.help.splitBetween')} />
            </label>
            <ParticipantPicker
                participants={participants}
                selected={splitBetween}
                onChange={(next) => { setSplitBetween(next); setFormError(''); }}
                currentUser={currentUser}
                amount={enteredAmountVnd}
                shareRatios={shareRatios}
                disabled={isFormDisabled}
                ariaLabel={t('components.expenseForm.splitBetween')}
            />
        </div>
    );

    // ── Shared form fields ──
    const formFields = (
        <>
            {/* Inline Error Message */}
            {formError && (
                <div
                    className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center"
                    role="alert"
                    aria-live="polite"
                >
                    <FontAwesomeIcon icon={faExclamationCircle} className="text-red-600 dark:text-red-400 mr-2" aria-hidden="true" />
                    <span className="text-sm text-red-700 dark:text-red-300">{formError}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" aria-describedby={formError ? "form-error" : undefined}>
                {splitFirst && splitBetweenField}

                {/* Amount */}
                <div>
                    <label htmlFor="expense-amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('components.expenseForm.amount')} <span className="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <AmountInput
                        id="expense-amount"
                        value={amount}
                        onChange={(next) => { setAmount(next); setFormError(''); }}
                        placeholder={t('components.expenseForm.amountPlaceholder')}
                        placeholderThousand={t('components.expenseForm.amountPlaceholderThousand')}
                        required
                        disabled={isFormDisabled}
                    />
                </div>

                {/* Description */}
                <div>
                    <label htmlFor="expense-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('components.expenseForm.description')} <span className="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="expense-description"
                        type="text"
                        value={description}
                        onChange={(e) => { setDescription(e.target.value); setFormError(''); }}
                        className="input-field"
                        placeholder={t('components.expenseForm.descriptionPlaceholder')}
                        required
                        aria-required="true"
                        disabled={isFormDisabled}
                    />
                </div>

                {/* Date */}
                <div>
                    <label htmlFor="expense-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" aria-hidden="true" />
                        {t('components.expenseForm.date')}
                    </label>
                    <input
                        id="expense-date"
                        type="date"
                        value={expenseDate}
                        onChange={(e) => { setExpenseDate(e.target.value); setFormError(''); }}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        disabled={isFormDisabled}
                    />
                </div>

                {/* Who Paid */}
                <div>
                    <label htmlFor="expense-payer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <FontAwesomeIcon icon={faUserCheck} className="mr-2" aria-hidden="true" />
                        {t('components.expenseForm.whoPaid')}
                        <HelpTip text={t('components.expenseForm.help.whoPaid')} />
                    </label>
                    <select
                        id="expense-payer"
                        value={payer}
                        onChange={(e) => { setPayer(e.target.value); setFormError(''); }}
                        className="input-field"
                        required
                        aria-required="true"
                        disabled={isFormDisabled}
                    >
                        <option value="">{t('components.expenseForm.selectPayer')}</option>
                        {participantIds.map(participantId => (
                            <option key={participantId} value={participantId}>
                                {participants[participantId]}
                            </option>
                        ))}
                    </select>
                </div>

                {!splitFirst && splitBetweenField}

                {/* Buttons */}
                {isEditMode ? (
                    <div className="flex space-x-3 pt-4 form-sticky-actions">
                        <button
                            type="submit"
                            disabled={isFormDisabled || splitBetween.length === 0}
                            className="btn-primary flex-1 flex items-center justify-center"
                        >
                            {isFormDisabled ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                    {t('event.components.editExpenseForm.savingChanges')}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                                    {t('event.components.editExpenseForm.saveChanges')}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isFormDisabled}
                            className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faTimes} className="mr-2" />
                            {t('event.components.editExpenseForm.cancel')}
                        </button>
                    </div>
                ) : (
                    /* In the sheet this wrapper is what gets pinned to the bottom
                       of the scroll; everywhere else it is an inert div. */
                    <div className={bare ? 'form-sticky-actions' : undefined}>
                        <button
                            type="submit"
                            disabled={isSubmitting || splitBetween.length === 0}
                            className="btn-primary w-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            aria-disabled={isSubmitting || splitBetween.length === 0}
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" aria-hidden="true" />
                                    <span>{t('components.expenseForm.adding')}</span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPlus} className="mr-2" aria-hidden="true" />
                                    <span>{t('components.expenseForm.addExpense')}</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </form>
        </>
    );

    // ── Edit mode: render as modal ──
    // Rendered via portal to body. In place it would sit inside the page's
    // content wrapper, which `.glass-page-bg > *` makes a stacking context at
    // z-index 1 — so the overlay's own z-index never gets past the sticky
    // header (z-50) or the phone's bottom action bar (z-40). Those two then
    // cover the close button and the pinned Save button, and once the form is
    // long enough to scroll, nothing can bring Save out from under the bar.
    if (isEditMode) {
        return createPortal(
            <div className="glass-modal-overlay is-fullscreen-mobile">
                <div className="glass-modal glass-modal-form max-w-lg w-full">
                    {/* Header stays put while the form scrolls under it — on a
                        phone this is the only way back out. */}
                    <div className="glass-modal-header">
                        <div className="flex items-center min-w-0">
                            <FontAwesomeIcon icon={faEdit} className="text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0" />
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                                {t('event.components.editExpenseForm.title')}
                            </h2>
                        </div>
                        <button
                            onClick={onCancel}
                            className="glass-header-back !p-2 flex-shrink-0"
                            aria-label={t('event.expenseSection.close')}
                            disabled={isFormDisabled}
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-sm" />
                        </button>
                    </div>
                    <div className="glass-modal-body has-sticky-actions">
                        {formFields}
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    // ── Create mode inside a sheet: the sheet header already names the form ──
    if (bare) {
        return formFields;
    }

    // ── Create mode: render as inline card ──
    return (
        <div className="card mb-6">
            <div className="flex items-center mb-4">
                <FontAwesomeIcon icon={faReceipt} className="text-blue-600 dark:text-blue-400 mr-2" aria-hidden="true" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t('components.expenseForm.title')}
                </h2>
            </div>
            {formFields}
        </div>
    );
};

export default ExpenseForm;